import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Button, Form, Divider, Alert } from 'antd';
import DspxPopover from '../../../../DspxPopover/DspxPopover';
import PaymentTypesField from '../PaymentTypesField';
import { createKey as createCurrentPaymentTypesKey } from '../../api/currentPaymentTypesQuery';
import { useSetPaymentTypes } from '../../api/paymentTypesMutation';
import { useActions } from '../../model/state';
import { getUpdatedRequest } from '../../api/getUpdatedRequest';
import styles from './ChangePaymentTypes.module.scss';

export const areArraysEqual = (arr1, arr2) => {
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);

  if (set1.size !== set2.size) return false;

  for (let item of set1) {
    if (!set2.has(item)) return false;
  }

  return true;
};

const PopoverForm = ({
  form,
  requestId,
  onCancel,
  initialValue,
  onFail,
  onSuccess,
}) => {
  const { getFieldDecorator, validateFields, resetFields } = form;

  const queryClient = useQueryClient();

  const { onSuccessRef, onEntityUpdate } = useActions();

  const key = createCurrentPaymentTypesKey({ requestId });

  const { setAsync, loading } = useSetPaymentTypes({
    onSuccess: async ({ requestId, res }) => {
      const updatedRequest = await getUpdatedRequest(requestId);

      if (typeof onSuccessRef.current === 'function') {
        onSuccessRef.current(updatedRequest);
      }

      onEntityUpdate(updatedRequest);

      if (res.success) {
        typeof onSuccess === 'function' && onSuccess();
        await queryClient.invalidateQueries(key);
      } else {
        onFail(res.message || 'Не удалось изменить способы оплаты');
      }
    },
  });

  const handleCancel = () => {
    onCancel();
    resetFields();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    validateFields(async (err, values) => {
      if (!err) {
        const { paymentTypes } = values;

        if (!areArraysEqual(paymentTypes, initialValue)) {
          await setAsync({ requestId, data: values });
        }

        handleCancel();
      }
    });
  };

  return (
    <Form
      colon={false}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      <PaymentTypesField
        id={requestId}
        getFieldDecorator={getFieldDecorator}
        initVal={initialValue}
        showLabel={false}
      />

      <Divider style={{ margin: 0 }} />

      <div className={styles.actions}>
        <Button
          disabled={loading}
          onClick={handleCancel}
        >
          Отмена
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={loading}
        >
          Изменить
        </Button>
      </div>
    </Form>
  );
};

const PopoverFormContent = Form.create({ name: 'change_payment_types' })(
  PopoverForm
);

const ChangePaymentTypes = ({
  requestId,
  currentPaymentTypes,
  changeFail,
  changeSuccess,
}) => {
  const { onVisibleTypesPopoverChange } = useActions();

  const [visible, setVisible] = useState(false);
  const [fail, setFail] = useState();

  const handleVisibleChange = (visible) => {
    setVisible(visible);
    onVisibleTypesPopoverChange(visible);
  };

  const handleFail = (msg) => {
    setFail(msg);
    if (typeof changeFail === 'function') {
      changeFail();
    }
  };

  return fail && !visible ? (
    <Alert
      message={fail}
      type="info"
      showIcon
    />
  ) : (
    <DspxPopover
      content={
        <PopoverFormContent
          key={visible}
          requestId={requestId}
          onCancel={() => handleVisibleChange(false)}
          initialValue={currentPaymentTypes}
          onFail={handleFail}
          onSuccess={changeSuccess}
        />
      }
      title="Изменить способ оплаты"
      trigger="click"
      visible={visible}
      onVisibleChange={handleVisibleChange}
      placement="right"
      arrow={false}
    >
      <Button>Изменить способ оплаты</Button>
    </DspxPopover>
  );
};

export default ChangePaymentTypes;
